/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The distribution channel.
 */
export const Channel = {
  Plugin: "plugin",
  Assistant: "assistant",
} as const;
/**
 * The distribution channel.
 */
export type Channel = ClosedEnum<typeof Channel>;

/**
 * An active plugin or assistant distribution of a project skill.
 */
export type SkillDistribution = {
  /**
   * The assistant that carries the skill.
   */
  assistantId?: string | undefined;
  /**
   * The name of the assistant that carries the skill.
   */
  assistantName?: string | undefined;
  /**
   * The distribution channel.
   */
  channel: Channel;
  /**
   * When the distribution was created.
   */
  createdAt: Date;
  /**
   * The user that created the distribution.
   */
  createdByUserId: string;
  /**
   * The distribution ID.
   */
  id: string;
  /**
   * The pinned version, absent when tracking the latest valid version.
   */
  pinnedVersionId?: string | undefined;
  /**
   * The plugin that carries the skill.
   */
  pluginId?: string | undefined;
  /**
   * The name of the plugin that carries the skill.
   */
  pluginName?: string | undefined;
  /**
   * The project that owns the distribution.
   */
  projectId: string;
  /**
   * The version currently targeted by this distribution.
   */
  resolvedVersionId: string;
  /**
   * The display name of the distributed skill.
   */
  skillDisplayName: string;
  /**
   * The distributed skill ID.
   */
  skillId: string;
  /**
   * The canonical name of the distributed skill.
   */
  skillName: string;
  /**
   * When the distribution configuration last changed.
   */
  updatedAt: Date;
};

/** @internal */
export const Channel$inboundSchema: z.ZodMiniEnum<typeof Channel> = z.enum(
  Channel,
);

/** @internal */
export const SkillDistribution$inboundSchema: z.ZodMiniType<
  SkillDistribution,
  unknown
> = z.pipe(
  z.object({
    assistant_id: z.optional(z.string()),
    assistant_name: z.optional(z.string()),
    channel: Channel$inboundSchema,
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    created_by_user_id: z.string(),
    id: z.string(),
    pinned_version_id: z.optional(z.string()),
    plugin_id: z.optional(z.string()),
    plugin_name: z.optional(z.string()),
    project_id: z.string(),
    resolved_version_id: z.string(),
    skill_display_name: z.string(),
    skill_id: z.string(),
    skill_name: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "assistant_id": "assistantId",
      "assistant_name": "assistantName",
      "created_at": "createdAt",
      "created_by_user_id": "createdByUserId",
      "pinned_version_id": "pinnedVersionId",
      "plugin_id": "pluginId",
      "plugin_name": "pluginName",
      "project_id": "projectId",
      "resolved_version_id": "resolvedVersionId",
      "skill_display_name": "skillDisplayName",
      "skill_id": "skillId",
      "skill_name": "skillName",
      "updated_at": "updatedAt",
    });
  }),
);

export function skillDistributionFromJSON(
  jsonString: string,
): SafeParseResult<SkillDistribution, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SkillDistribution$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SkillDistribution' from JSON`,
  );
}
